
import { mapGetters, mapActions } from 'vuex'
import productData from '@/data/products.json'
import reviewData from '@/data/reviews.json'

// Helper function to extract slug from URI
function getSlugFromUri(uri) {
  if (!uri) return '';
  const parts = uri.split('/');
  // Assuming slug is the second to last part, e.g., /courses/advanced/my-course-slug/
  // Handle cases where URI might end with a slash or not.
  const filteredParts = parts.filter(part => part !== '');
  return filteredParts.length > 0 ? filteredParts[filteredParts.length - 1] : '';
}

  export default {
    name: "CourseCardsAdvanced",
    props: {
        region: {
            default: 'uk',
            type: String,
        }
    },
    data() {
        return {
            isActive: this.active !== '',
            // Hardcoded product IDs for the advanced courses.
            // These IDs are used to filter productData and generate structured data.
            productIds: ["10203", "10200", "10192", "10191", "10189", "10179", "105", "106", "10161"],
            reviews: reviewData,
            // Explicit mapping for modal IDs, as they are not directly in productData.json
            modalIdMap: {
                "10203": "aiincludedcard",
                "10200": "neuroincludedcard",
                "10192": "observeincludedcard",
                "10191": "teoincludedcard",
                "10189": "eapincludedcard",
                "10179": "examprepincludedcard",
                "105": "tylincludedcard",
                "106": "beincludedcard",
                "10161": "otpincludedcard",
            },
            // Map to override descriptions that are not correctly represented in productData.json
            courseDescriptionsOverride: {
                "10203": "Learn how to use AI to support English language students with this advanced TEFL course.",
                "10200": "Learn how to support neurodiverse students with conditions such as ADHD, dyslexia and autism with this advanced TEFL course.",
                "10192": "Improve your skills in lesson planning and delivery and gain valuable feedback from an experienced EFL teacher with this advanced TEFL course.",
                "10191": "Become an online English teacher and work from home - or wherever there’s an internet connection! - with our Teaching English Online course.",
                "10189": "Become certified to teach English for Academic Purposes (EAP) lessons with this Advanced TEFL course. Work with learners applying for, or studying at, English-speaking universities.",
                "10179": "Millions sit English language proficiency exams every single year. Tap into this huge market with our Teaching Exam Preparation Classes course and learn how to prepare students for a range of popular exams, such as the IELTS and TOEFL.",
                "105": "Our Teaching Young Learners course is a must for anyone aiming to work with children up to the age of 10. Make learning fun and keep young learners engaged.",
                "106": "Break into the lucrative world of Business English with this course. Learn how to develop lessons tailored to the specific needs of business learners.",
                "10161": "Get practical experience teaching English online with our Online Teaching Practice. Practice delivering lessons in front of a tutor and your peers.",
            },
            // Map to override course durations not available in productData.json (ISO 8601 format)
            courseDurationOverride: {
                "10203": "P1M", // 1 Month
                "10200": "P1M", // 1 Month
                "10192": "P2M", // 2 Months
                "10191": "P2M", // 2 Months
                "10189": "P2M", // 2 Months
                "10179": "P2M", // 2 Months
                "105": "P1M", // 1 Month
                "106": "P1M", // 1 Month
                "10161": "P3D", // 3 Days
            },
        }
    },
     head() {
      if(this.page === 'advanced') {
        return {
            script: [{ type: 'application/ld+json', json: this.structuredData }]
        }
      }
    },
    computed: {
      ...mapGetters({
          activeModal : 'pages/getActiveModal',
          page: 'pages/getPage'
      }),
      products() {
        // Filter productData based on the defined productIds.
        const products = this.productIds.map((id) => {
            return productData.find((product) => {
                return product.id.toString() === id
            })
        }).filter(Boolean); // Filter out any undefined products if an ID doesn't match
        return products
      },
      // New computed property to prepare data for template rendering
      courseDisplayData() {
          return this.products.map(product => {
              const modalId = this.modalIdMap[product.id.toString()] || '';
              const description = this.courseDescriptionsOverride[product.id.toString()] || product.description;

              // Determine badge dynamically based on product ID or name if possible
              let badge = '';
              if (product.id.toString() === "10203" || product.id.toString() === "10200" || product.id.toString() === "10192") {
                  badge = "New course!";
              } else if (product.id.toString() === "10191") {
                  badge = "New and improved!";
              }

              return {
                  ...product, // Spread existing product data
                  modalId,
                  badge,
                  description, // Use the overridden or fallback description
                  dataCy: `go-to-course-${modalId.replace('includedcard', '')}`, // Generate data-cy
                  // Ensure image and slug are directly accessible for template props
                  image: product.page?.[this.region]?.thumbnail || product.page?.en?.thumbnail || product.page?.en_us?.thumbnail || 'https://placehold.co/400x200/cccccc/333333?text=No+Image',
                  slug: getSlugFromUri(product.page?.[this.region]?.uri || product.page?.en?.uri || product.page?.en_us?.uri),
                  alt: product.name, // Using product name as alt text fallback
              };
          });
      },
      // New computed property to find the currently active modal's course data
      currentActiveModalCourse() {
          if (!this.activeModal) {
              return null;
          }
          // Find the course in courseDisplayData that matches the activeModal
          return this.courseDisplayData.find(course => course.modalId === this.activeModal);
      },
      priceData() {
        const priceDataArray = []
        this.products.forEach((product) => {
          const priceD = this.$options.filters.priceData(product)
          priceDataArray.push(priceD)
        })
        return priceDataArray
      },
      prices(){
          const prices = []
          this.priceData.forEach((priceData) => {
            const price = this.$nuxt.$options.filters.priceFromLabelNumber(priceData.pricesDiscounted)
            prices.push(price)
          })
          return prices
        },
      currency(){
          const currency = this.$nuxt.$options.filters.currencyCodeFromLabel()
          return currency
        },
      structuredData() {
         const averageRating = this.$nuxt.$options.filters.roundScore(Number(this.reviews.stats.company.average_rating))
          const ratingCount = this.reviews.stats.company.review_count
          const baseUrl = this.$props.region === 'uk'
            ? "https://www.tefl.org/courses/advanced/"
            : "https://www.tefl.org/en-us/courses/advanced/";

          // Common provider details for all courses
          const commonProvider = {
            "@id": "https://www.tefl.org/#educationalorganization",
            "@type": "Organization",
            "name": "The TEFL Org",
            "logo": "https://www.tefl.org/_nuxt/images/tefl-org-web_strapline-ee5f8b.svg",
            "url": "https://www.tefl.org/",
            "sameAs": [ "https://www.facebook.com/teflorg", "https://x.com/TEFLOrg", "https://www.instagram.com/teflorg/" , "https://www.linkedin.com/company/teflorg/" ],
          };

          // Common audience details for all courses
          const commonAudience = {
            "@type": "Audience",
            "audienceType": [
              "People who are serious about teaching English as a foreign language.",
              "Those who are interested in the opportunity to work in most countries in the world.",
              "Professionals who want to re-educate themselves.",
              "Students who are considering a gap year.",
              "Anyone who would like a career teaching online."
            ]
          };

          // Common educational credential awarded details for all courses
          const commonEducationalCredential = {
            "@type": "EducationalOccupationalCredential",
            "name": "Tefl Org Certificate",
            "credentialCategory": "Certificate",
            "url": "https://www.tefl.org/about-us/accreditation/",
            "educationalLevel": "advanced"
          };

          // Define the common prerequisite string directly
          const commonPrerequisite = "It would be beneficial to have an initial TEFL qualification (e.g. 120-hr Premier Online TEFL Course) prior to taking this course.";

          // Map over the 'products' array to generate structured data for each course
          const itemListElement = this.products.map((product, index) => { // Changed 'course' to 'product' for clarity here
            const coursePrice = this.prices[index] ? this.prices[index].toString() : "0"; // Fallback to "0" if price not found

            // Extract image URL from the 'page' object, prioritizing region-specific then general 'en' or 'en_us'
            const courseImage = product.page?.[this.region]?.thumbnail || product.page?.en?.thumbnail || product.page?.en_us?.thumbnail || 'https://placehold.co/400x200/cccccc/333333?text=No+Image';

            // Extract slug from the 'page' object's URI
            const courseSlug = getSlugFromUri(product.page?.[this.region]?.uri || product.page?.en?.uri || product.page?.en_us?.uri);

            // Derive timeRequired and courseWorkload from 'course.hours' if available
            const courseTimeRequired = product.course?.hours ? `PT${product.course.hours}H` : '';
            const courseWorkload = product.course?.hours ? `PT${product.course.hours}H` : '';

            // Determine courseMode based on specific product ID for 'Online Teaching Practice'
            let courseMode = "online";
            if (product.id.toString() === '10161') { // ID for Online Teaching Practice
                courseMode = "onsite";
            }

            // Use the overridden description if available, otherwise fallback to product.description
            const description = this.courseDescriptionsOverride[product.id.toString()] || product.description;
            // Use the overridden duration if available, otherwise fallback to empty string
            const duration = this.courseDurationOverride[product.id.toString()] || '';


            return {
              "position": (index + 1).toString(),
              "@type": "Course",
              "name": product.name,
              "image": courseImage,
              "url": `${baseUrl}${courseSlug}/`,
              description, // Use the determined description
              // Fields derived from productData.json
              "timeRequired": courseTimeRequired,
              "learningResourceType": "Course",
              // Use the hardcoded common prerequisite
              "coursePrerequisites": commonPrerequisite,
              "offers": {
                "@type": "offer",
                "category": "Paid",
                "price": coursePrice,
                "priceCurrency": this.currency,
                "availability": "https://schema.org/InStock",
              },
              "provider": commonProvider,
              "hasCourseInstance": {
                "@type": "CourseInstance",
                courseMode,
                courseWorkload,
                duration, // Use the determined duration
              },
              "inLanguage": "en",
              "interactivityType": "active",
              "educationalCredentialAwarded": commonEducationalCredential,
              "audience": commonAudience,
              "about": "Teaching English as a Foreign Language",
              "aggregateRating": {
                "@type": "AggregateRating",
                "ratingValue": averageRating,
                ratingCount,
                "bestRating": 5
              },
            };
          });

          return {
            "@context": "https://schema.org/",
            "@type": "ItemList",
            itemListElement
          };
      }
    },
    methods:{
        ...mapActions({
            updateActiveModal: 'pages/updateActiveModal',
        }),
        updateModal(id){
            this.updateActiveModal(id)
            console.log('Active Modal: ',this.activeModal)
        },
        hideModal(){
            this.updateActiveModal(null)
            console.log('Active Modal: ',this.activeModal)
        }
    },
    render() {
      const slot = this.$slots.default
      return slot
    }

  }
  